import { Input } from '@src/components/ui';
import { INPUT_CONFIG } from '@src/constants/inputConfig';
import { isFormValidationError } from '@src/utilities/form';
import { sanitizeNumberInput } from '@src/utilities/input';
import { useCallback } from 'react';
import { Controller, useFormState } from 'react-hook-form';

import { ICommonFormProps } from '../DynamicForm.types';

export function FormNumberInput(props: Readonly<ICommonFormProps>) {
    const {
        label,
        keyName,
        control,
        registerOptions,
        inputProperties,
        formProps,
        disabled,
        className,
    } = props;

    const createHandleChange = useCallback(
        (onChange: (value: any) => void) =>
            (e: React.ChangeEvent<HTMLInputElement>) => {
                const maxLength =
                    formProps?.maxLength ?? INPUT_CONFIG.NUMBER.maxLength;
                const value = sanitizeNumberInput(e.target.value, maxLength);

                if (value !== '') {
                    if (inputProperties?.min !== undefined) {
                        if (value < inputProperties.min) return;
                    }
                    if (inputProperties?.max !== undefined) {
                        if (value > inputProperties.max) return;
                    }
                }

                onChange(value === '' ? null : value);

                if (registerOptions?.onChange) {
                    const sanitizedEvent = {
                        ...e,
                        target: {
                            ...e.target,
                            value,
                        },
                    } as React.ChangeEvent<HTMLInputElement>;
                    registerOptions.onChange(sanitizedEvent);
                }
            },
        [registerOptions, formProps]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === '-') e.preventDefault();
            inputProperties?.onKeyDown?.(e);
        },
        [inputProperties?.onKeyDown]
    );

    const { touchedFields, isSubmitted } = useFormState({ control });

    return (
        <div className="w-full">
            <Controller
                control={control}
                name={keyName}
                rules={registerOptions}
                render={({ field, fieldState: { error: fieldStateError } }) => {
                    const { size, ...inputProps } = inputProperties || {};

                    // Use filtered errors prop if available, otherwise use fieldState error
                    // But only show if field has been touched or form submitted
                    const error = props.errors ?? fieldStateError;
                    const showError = Boolean(
                        error && (touchedFields[keyName] || isSubmitted)
                    );
                    const errorMessage = showError
                        ? isFormValidationError(error?.type)
                            ? error?.message
                            : `${label} ${error?.type}`
                        : undefined;

                    return (
                        <Input
                            className={`h-10 text-base ${className ?? ''}`}
                            disabled={disabled || false}
                            label={label}
                            error={errorMessage}
                            data-component-name={keyName}
                            {...inputProps}
                            size={size}
                            placeholder={inputProperties?.placeholder || ''}
                            type="number"
                            step="any"
                            min={inputProperties?.min}
                            max={inputProperties?.max}
                            value={field.value ?? null}
                            onChange={createHandleChange(field.onChange)}
                            onBlur={field.onBlur}
                            onKeyDown={handleKeyDown}
                            isRequired={
                                typeof registerOptions?.required === 'object'
                                    ? Boolean(registerOptions.required.value)
                                    : Boolean(registerOptions?.required)
                            }
                            defaultValue={formProps?.defaultValue}
                            maxLength={formProps?.maxLength}
                            showCount={formProps?.showCount}
                            addonBefore={formProps?.addonBefore}
                            addonAfter={formProps?.addonAfter}
                            help={formProps?.help}
                            allowClear={formProps?.allowClear}
                            suffix={formProps?.suffix}
                        />
                    );
                }}
            />
        </div>
    );
}
